import { useState, useEffect } from 'react';
import { X, Plus, Edit, Calendar, Tag, CreditCard } from 'lucide-react';
import { Transaction } from '../../types';

interface TransactionFormProps {
  onSave: (transaction: Omit<Transaction, 'id'>) => void;
  onCancel: () => void;
  transaction?: Transaction;
}

const categories = [
  'Food & Dining',
  'Transportation',
  'Shopping',
  'Entertainment',
  'Bills & Utilities',
  'Healthcare',
  'Education',
  'Salary',
  'Freelance',
  'Investments',
  'Other'
];

const paymentMethods = [
  'Cash',
  'Credit Card',
  'Debit Card',
  'Bank Transfer',
  'Digital Wallet',
  'Other'
];

export default function TransactionForm({ onSave, onCancel, transaction }: TransactionFormProps) {
  const [formData, setFormData] = useState({
    amount: transaction?.amount || 0,
    description: transaction?.description || '',
    category: transaction?.category || 'Other',
    type: transaction?.type || 'expense',
    date: transaction?.date || new Date().toISOString().split('T')[0],
    paymentMethod: transaction?.paymentMethod || 'Cash',
    tags: transaction?.tags || [],
  });

  // Update form when transaction prop changes
  useEffect(() => {
    if (transaction) {
      setFormData({
        amount: transaction.amount,
        description: transaction.description,
        category: transaction.category,
        type: transaction.type,
        date: transaction.date,
        paymentMethod: transaction.paymentMethod || 'Cash',
        tags: transaction.tags || [],
      });
    }
  }, [transaction]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.amount <= 0) {
      alert('Amount must be greater than 0');
      return;
    }
    
    const transactionData = {
      ...formData,
      amount: formData.type === 'expense' ? Math.abs(formData.amount) : Math.abs(formData.amount)
    };
    
    onSave(transactionData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value,
    }));
  };

  const addTag = () => {
    const tag = prompt('Enter a tag:');
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">
            {transaction ? 'Edit Transaction' : 'Add Transaction'}
          </h2>
          <p className="text-gray-400 text-sm">Enter transaction details</p>
        </div>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-800/50 rounded-lg transition-colors"
          type="button"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Type Selector */}
        <div className="flex space-x-2 mb-4">
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, type: 'income' }))}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
              formData.type === 'income'
                ? 'bg-gradient-to-r from-green-500/20 to-green-600/20 text-green-400 border border-green-500/30 shadow-inner'
                : 'bg-gray-900/50 text-gray-400 border border-gray-800 hover:bg-gray-800/50'
            }`}
          >
            Income
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, type: 'expense' }))}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
              formData.type === 'expense'
                ? 'bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-400 border border-red-500/30 shadow-inner'
                : 'bg-gray-900/50 text-gray-400 border border-gray-800 hover:bg-gray-800/50'
            }`}
          >
            Expense
          </button>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              type="number"
              name="amount"
              value={formData.amount || ''}
              onChange={handleChange}
              step="0.01"
              min="0.01"
              className="input-field w-full pl-10 pr-4"
              required
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="input-field w-full"
            rows={2}
            required
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Category
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="input-field w-full"
          >
            {categories.map(category => (
              <option key={category} value={category} className="bg-gray-900">
                {category}
              </option>
            ))}
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>Date</span>
            </div>
          </label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="input-field w-full"
            required
          />
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <div className="flex items-center space-x-2">
              <CreditCard className="w-4 h-4" />
              <span>Payment Method</span>
            </div>
          </label>
          <select
            name="paymentMethod"
            value={formData.paymentMethod}
            onChange={handleChange}
            className="input-field w-full"
          >
            {paymentMethods.map(method => (
              <option key={method} value={method} className="bg-gray-900">
                {method}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Tag className="w-4 h-4" />
                <span>Tags</span>
              </div>
              <button
                type="button"
                onClick={addTag}
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                + Add Tag
              </button>
            </div>
          </label>
          <div className="flex flex-wrap gap-2">
            {formData.tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-800 text-gray-300 border border-gray-700"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-2 text-gray-500 hover:text-gray-300"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex space-x-3 pt-6 border-t border-gray-800">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 px-4 bg-gray-900/50 text-gray-300 rounded-xl font-medium hover:bg-gray-800/50 transition-all duration-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 py-3 px-4 btn-primary flex items-center justify-center"
          >
            {transaction ? (
              <>
                <Edit className="w-4 h-4 mr-2" />
                Update Transaction
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add Transaction
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}